import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Award, Radio, PlayCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard, Money } from "@/components/common";

export default function LearnerDashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [results, setResults] = useState([]);
  const [live, setLive] = useState([]);

  useEffect(() => {
    api.get("/my/enrollments").then(({ data }) => setEnrollments(data));
    api.get("/my/results").then(({ data }) => setResults(data));
    api.get("/my/live-classes").then(({ data }) => setLive(data));
  }, []);

  const avgScore = results.length ? Math.round(results.reduce((s, r) => s + r.percentage, 0) / results.length) : 0;

  return (
    <div data-testid="learner-dashboard">
      <div className="viriks-navy rounded-2xl p-8 text-white mb-8 relative overflow-hidden">
        <div className="absolute inset-0 hero-noise opacity-30" />
        <h1 className="font-heading text-3xl font-bold relative">Hi, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-white/70 mt-1 relative">Continue where you left off and keep the streak going.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <StatCard label="Enrolled Courses" value={enrollments.length} icon={BookOpen} testid="stat-courses" />
        <StatCard label="Tests Taken" value={results.length} icon={Award} accent="bg-amber" testid="stat-tests" />
        <StatCard label="Avg Score" value={`${avgScore}%`} icon={Award} accent="bg-crimson" />
        <StatCard label="Upcoming Live" value={live.filter((l) => l.status === "scheduled").length} icon={Radio} accent="bg-emerald-600" />
      </div>

      <h2 className="font-heading text-xl font-bold text-navy mb-4">Continue Learning</h2>
      {enrollments.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-10 text-center">
          <BookOpen size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-muted-foreground">No courses yet. <Link to="/courses" className="text-amber font-semibold">Browse courses</Link></p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {enrollments.slice(0, 6).map((e) => (
            <Link key={e.id} to={e.expired ? `/courses/${e.course_id}` : `/learner/learn/${e.course_id}`} className="bg-white border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all" data-testid={`enrolled-${e.course_id}`}>
              <div className="relative aspect-video"><img src={e.course.thumbnail} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center"><PlayCircle size={40} className="text-white" /></div>
                {e.expired && <span className="absolute top-2 right-2 bg-crimson text-white text-xs px-2 py-1 rounded-full">Expired</span>}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-navy line-clamp-2 text-sm">{e.course.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{e.expired ? "Access expired — renew" : "Continue learning"} <ArrowRight size={12} className="inline" /></p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
